import get from 'lodash.get'
import { action, observable, toJS } from 'mobx'
import qs from 'querystring'
import url from 'url'
import { BaseStore, getApiURL, getOrCreateStore } from '../di'

// Don't use this store.
// To be removed after we migrate OrgAdmin Newsletters and Requests tabs
// to functional components with Recoil state fetching data from restApi client.
// This is what we have done with OrgAdmin Organizations and Users tabs already.
class AdminStore extends BaseStore {
  @observable organizations = []
  @observable hubs = []
  @observable users = []
  @observable newsletters = []
  @observable requests = []
  @observable isFetching = false
  @observable fetchingError = false
  @observable searching = false

  @observable organizationsUploaded = false
  @observable hubsUploaded = false
  @observable usersUploaded = false
  @observable newslettersUploaded = false
  @observable requestsUploaded = false

  @observable prevURL = ''
  @observable nextURL = ''

  @action
  setNext = (url) => {
    this.nextURL = url
  }

  @action
  setPrev = (url) => {
    this.prevURL = url
  }

  hasNext = () => {
    return this.nextURL !== ''
  }

  hasPrev = () => {
    return this.prevURL !== ''
  }

  next = async () => {
    if (!this.hasNext()) {
      throw new Error('no more page left')
    }

    return this.fetchMoreRequest(buildRequest(this.nextURL))
  }

  prev = async (jwt) => {
    if (!this.hasPrev()) {
      throw new Error('no previous page')
    }

    return this.fetchMoreRequest(buildRequest(this.prevURL), jwt)
  }

  @action
  setEntityUploaded = (entityType) => {
    this[`${entityType}Uploaded`] = true
  }

  @action
  setEntity = (type, data) => {
    this[type] = data
  }

  fetchMoreRequest = async (options) => {
    const { jwt, clientURL, ssrURL, csrfToken } = window?.__NEXT_DATA__?.props?.restApiSettings
    const apiUrl = getApiURL(false, clientURL, ssrURL)

    const resp = await this.request(options, jwt, apiUrl, csrfToken)

    this.setNext(get(resp, 'links.next', ''))
    this.setPrev(get(resp, 'links.next', ''))

    return resp
  }

  searchEntity = async (q, entityType) => {
    this.setNext('')
    this.setPrev('')

    this.setSearching(true)

    const { jwt, clientURL, ssrURL, csrfToken } = window?.__NEXT_DATA__?.props?.restApiSettings
    const apiUrl = getApiURL(false, clientURL, ssrURL)

    try {
      const resp = await this.request(
        {
          path: `/orgadmin/${entityType}`,
          query: { q },
        },
        jwt,
        apiUrl,
        csrfToken
      )

      this.setEntity(entityType, resp.data)
      this.setNext(get(resp, 'links.next', ''))
      this.setPrev(get(resp, 'links.next', ''))

      this.setSearching(false)
    } catch (err) {
      console.log(err)
      this.setSearching(false)
    }
  }

  @action
  setUsers = (data) => {
    this.users = data
  }

  @action
  setIsFetching = (isFetching) => {
    this.isFetching = isFetching
  }

  @action
  setSearching = (searching) => {
    this.searching = searching
  }

  @action
  setFetchingError = (err) => {
    this.fetchingError = err
  }

  fetchEntity = async (q, entityType) => {
    this.setNext('')
    this.setPrev('')
    this.setFetchingError(false)

    try {
      const resp = await this.request({
        path: `/orgadmin/${entityType}`,
        query: { q },
      })

      this.setEntity(entityType, resp.data)

      this.setNext(get(resp, 'links.next', ''))
      this.setPrev(get(resp, 'links.next', ''))
      this.setIsFetching(false)
      this.setEntityUploaded(entityType)
    } catch (err) {
      this.setIsFetching(false)
      this.setFetchingError({
        status: err.status,
        statusText: err.statusText,
      })
    }
  }

  fetchMoreEntities = async (entityType) => {
    if (this.hasNext()) {
      try {
        const resp = await this.next()
        if (resp) {
          const result = [...this[entityType], ...resp.data]
          this.setEntity(entityType, result)
        }
        this.setIsFetching(false)
      } catch (err) {
        console.log(err)
        this.setIsFetching(false)
      }
    }
  }

  getEntity = (type) => {
    return toJS(this[type])
  }

  @action
  deleteItemFromEntity = (id, entityType) => {
    const index = this[entityType].findIndex((item) => item.id === id)
    this[entityType].splice(index, 1)
  }

  deleteOrganization = async (id) => {
    await this.request({
      path: `/orgadmin/organizations/${id}`,
      method: 'DELETE',
    })
  }

  deleteHub = async (id) => {
    await this.request({
      path: `/hubs/${id}`,
      method: 'DELETE',
    })
  }

  loginAs = async (user) => {
    await this.request({
      method: 'POST',
      path: `/orgadmin/users/${user.id}/impersonate`,
    })
  }

  rejectAdminRequest = async (request) => {
    await this.request({
      path: `/orgadmin/requests/${request.id}/reject`,
      method: 'PUT',
    })
  }

  approveAdminRequest = async (request) => {
    await this.request({
      path: `/orgadmin/requests/${request.id}/accept`,
      method: 'PUT',
    })
  }
}

const buildRequest = (path) => {
  const { pathname, query } = url.parse(path)

  if (!pathname) {
    throw new Error(`path not found at: ${path}`)
  }

  return {
    path: pathname,
    query: query ? qs.parse(query) : {},
  }
}

export default getOrCreateStore('AdminStore', AdminStore)
